import { useLocationsRealEstateByCoords } from "@/hooks/QueryHooks/useLocationsRealEstateByCoords";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMapContext } from "@/components/organisms/map/components/MapProvider";
import { useRef, useEffect, useMemo, useCallback } from "react";
import type { RealEstateType } from "@/types/real-estate";
type Props = {
  lat: number;
  lng: number;
  realEstateIds?: string[];
};
export function useMapListCluster(props: Props) {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const listEndRef = useRef<HTMLDivElement>(null);
  const {
    forcedMapTarget
  } = useMapContext();
  const {
    data: realEstate,
    isLoading,
    fetchNextPage,
    totalItems,
    hasNextPage,
    isFetchingNextPage
  } = useLocationsRealEstateByCoords({
    coords: {
      lat: props.lat,
      lng: props.lng
    },
    realEstateIds: props.realEstateIds
  });
  const realEstateData = useMemo(() => realEstate?.pages?.flatMap(page => page.data), [realEstate]);
  const sortedData = useMemo(() => {
    if (!realEstateData || !forcedMapTarget?.realEstateId) return realEstateData;
    const highlighted = realEstateData.filter(node => node.realEstate.id === forcedMapTarget.realEstateId);
    const rest = realEstateData.filter(node_0 => node_0.realEstate.id !== forcedMapTarget.realEstateId);
    return [...highlighted, ...rest];
  }, [realEstateData, forcedMapTarget]);
  const isHighlighted = (node_1: RealEstateType) => forcedMapTarget?.realEstateId === node_1.realEstate.id;
  const handleLoadMoreItems = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
    return;
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  useEffect(() => {
    let scrollContainer = containerRef?.current;
    let observer: MutationObserver | null = null;
    let attached = false;
    let retryCount = 0;
    const maxRetries = 5;
    function retryAttach() {
      if (retryCount >= maxRetries) return;
      retryCount++;
      scrollContainer = containerRef?.current;
      if (scrollContainer) {
        attachListener();
      } else {
        setTimeout(retryAttach, 100);
      }
    }
    function attachListener() {
      if (!scrollContainer || attached) return;
      if (scrollContainer.clientHeight === 0) {
        setTimeout(() => {
          scrollContainer = containerRef?.current;
          attachListener();
        }, 100);
        return;
      }
      attached = true;
      function onScroll() {
        if (!scrollContainer) return;
        const {
          scrollTop,
          scrollHeight,
          clientHeight
        } = scrollContainer;
        if (scrollHeight - scrollTop - clientHeight < 130) {
          handleLoadMoreItems();
        }
      }
      setTimeout(() => {
        if (scrollContainer) onScroll();
      }, 500);
      scrollContainer.addEventListener("scroll", onScroll);
      return () => {
        if (scrollContainer) {
          scrollContainer.removeEventListener("scroll", onScroll);
        }
      };
    }
    let cleanup: (() => void) | undefined;
    if (scrollContainer) {
      cleanup = attachListener();
    } else {
      retryAttach();
      observer = new MutationObserver(mutations => {
        scrollContainer = containerRef?.current;
        if (scrollContainer && !attached) {
          cleanup = attachListener();
          if (observer && attached) observer.disconnect();
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
    return () => {
      if (cleanup) cleanup();
      if (observer) observer.disconnect();
    };
  }, [handleLoadMoreItems, containerRef]);
  return {
    isMobile,
    containerRef,
    sortedData,
    isLoading,
    listEndRef,
    totalItems,
    isHighlighted
  };
}